import logger from '@/utils/logger.js'
import { auth, db } from '@/firebase.js'
import { createStore } from 'vuex';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, query, where, getDocs, onSnapshot, doc, getDoc, setDoc, orderBy } from 'firebase/firestore';
import { formatDistanceToNow } from 'date-fns'
import { de } from 'date-fns/locale'

// Wird einmalig erstellt, sobald initAuth zum ersten Mal aufgerufen wird.
// So können Router-Guard und startUp auf dieselbe Auth-Initialisierung warten.
let authReadyPromise = null

// Zentrale Admin-UID. Achtung: clientseitige Admin-Prüfung ist nur UI-Schutz –
// die eigentliche Absicherung müssen die Firestore Security Rules leisten.
export const ADMIN_UID = 'n3t7E4a3TJfrx2EPoTlqGdS5G9u2'

export default createStore({
  state: {
    User: null,
    Version: 'β 0.9.3',
    VersionVon: '17. Oktober 2026',

    isMobile: false,

    LoginText: '',
    DicerText: '',
    DicerTotal: '',
    snackbarText: '',

    showDicer: false,
    showSnackbar: false,
    showCharaktere: false,
    showEinstellungen: false,

    activeCharID: '',
    activeChar: null,
    activeKampagneID: '',
    activeKampagne: null,

    Arten: ['NPC+', 'NPC-', 'Kontakt', 'Gruppe', 'Ort', 'Kampage', 'Szene'],
    systeme: ['DSA 4.1', 'D&D 5e', 'Shadowrun 5e', 'Götterlicht'],

    Einstellungen: null,

    Nachrichten: [],
  },

  mutations: {
    setState(state, { key, value }) {
      state[key] = value
    },

    callSnackbar(state, value) {
      state.snackbarText = value
      state.showSnackbar = true
    },

    closeDicer(state) {
      state.showDicer = false
    },

    callDicer(state, payload) {
      if (!state.Einstellungen || state.Einstellungen.popupW) {
        state.DicerText = payload.result.replace("wirft: ", "").replace("wirft ", "")
        state.DicerTotal = payload.total
        state.showDicer = true
      }
    }
  },

  getters: {
    isAdmin: (state) => {
      return !!state.User && state.User.uid === ADMIN_UID
    },

    formatierteNachrichten: (state) => {
      return state.Nachrichten.map(nachricht => {
        if (nachricht.CreatedAt && nachricht.CreatedAt.toDate) {
          const datum = nachricht.CreatedAt.toDate()
          const formatiertesDatum = "(vor " + formatDistanceToNow(datum, { locale: de }) + ")"
          return { ...nachricht, formatiertesDatum }
        } else {
          return { ...nachricht, formatiertesDatum: 'n.a.' }
        }
      })
    }
  },

  actions: {
    async startUp() {
      await this.dispatch('initAuth')
      logger.log("Initialisierung der Authentifizierung gestartet...")
      await this.dispatch('ensureSettingsDocumentExists')
      logger.log("Initialisierung der Einstellungsdokumente abgeschlossen.")
      await this.dispatch('initEinstellungen')
      logger.log("Initialisierung der Einstellungen abgeschlossen")
      await this.dispatch('initMessages')
      logger.log("Initialisierung der Nachrichten abgeschlossen.")
      logger.log("Startup Prozess abgeschlossen.")
    },

    initAuth({ commit }) {
      // Einmalig den Auth-Listener registrieren und ein wiederverwendbares
      // Promise zurückgeben, das nach dem ersten Auth-Status-Wechsel auflöst.
      if (authReadyPromise) {
        return authReadyPromise
      }
      authReadyPromise = new Promise((resolve, reject) => {
        onAuthStateChanged(auth, (user) => {
          if (user === null) {
            commit('setState', { key: 'showCharaktere', value: false })
            commit('setState', { key: 'showEinstellungen', value: false })
            commit('setState', { key: 'LoginText', value: 'Nicht eingeloggt.' })
            commit('setState', { key: 'User', value: null })
          } else if (user.emailVerified) {
            commit('setState', { key: 'LoginText', value: user.displayName + ' du bist eingeloggt.' })
            commit('setState', { key: 'User', value: user })
          } else {
            commit('setState', { key: 'LoginText', value: user.displayName + ' deine E-Mail Adresse muss noch verifiziert werden.' })
            commit('setState', { key: 'User', value: user })
          }
          resolve()
        }, reject)
      })
      return authReadyPromise
    },

    async ensureSettingsDocumentExists({ state }) {
      if (state.User) {
        const userUid = state.User.uid
        const settingsDocRef = doc(db, "Einstellungen", userUid)
        try {
          const docSnap = await getDoc(settingsDocRef)
          if (!docSnap.exists()) {
            await setDoc(settingsDocRef, {
              User: userUid,
              discordWebhookURL: '',
              useDiscordWebhook: false,
              sessionId: userUid,
              popupW: true,
              popupDuration: 2000,
              autosaveN: true
            });
          }
        } catch (error) {
          console.error("Fehler beim Überprüfen/Erstellen des Einstellungen-Dokuments:", error)
        }
      }
    },

    async initEinstellungen({ commit, state }) {
      if (!state.User || !state.User.uid) {
        logger.log("Kein Benutzer UID gefunden.")
        return;
      }
      const userUid = state.User.uid
      const ColRefEinst = collection(db, 'Einstellungen');
      const q = query(ColRefEinst, where("User", "==", userUid))
      try {
        const querySnapshot = await getDocs(q)
        if (!querySnapshot.empty) {
          const docData = querySnapshot.docs[0].data()
          commit('setState', { key: 'Einstellungen', value: docData })
        } else {
          logger.log("Keine Einstellungen gefunden.")
        }
      } catch (error) {
        console.error("Fehler beim Laden der Einstellungen:", error)
      }
    },

    async initMessages({ commit, state }) {
      if (!state.User) {
        logger.log("In der Historie kein User gefunden.")
        return
      }
      if (!state.Einstellungen || !state.Einstellungen.sessionId) {
        logger.log("Keine sessionId verfügbar - Historie wird nicht geladen.")
        return
      }
      const q = query(
        collection(db, "Nachrichten"),
        where("sessionId", "==", state.Einstellungen.sessionId),
        orderBy("CreatedAt", "asc")
      )
      onSnapshot(q, (querySnapshot) => {
        const nachrichten = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        commit('setState', { key: 'Nachrichten', value: nachrichten })
      });
    },

  }
})